import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import api, { apiErr } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Plus, Car, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";
const empty = { patente: "", tipo: "moto", modelo: "", estado: "activo", repartidor_id: "" };

export default function Vehiculos() {
  const { setMobileOpen } = useOutletContext();
  const [vehiculos, setVehiculos] = useState([]);
  const [repartidores, setRepartidores] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => {
    api.get("/vehiculos").then(({ data }) => setVehiculos(data)).catch(() => {});
    api.get("/users").then(({ data }) => setRepartidores(data.filter((u) => ["repartidor", "comisionista"].includes(u.role)))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setShow(true); };
  const openEdit = (v) => { setEditing(v); setForm({ patente: v.patente, tipo: v.tipo, modelo: v.modelo || "", estado: v.estado, repartidor_id: v.repartidor_id || "" }); setShow(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/vehiculos/${editing.id}`, form); toast.success("Vehículo actualizado"); }
      else { await api.post("/vehiculos", form); toast.success("Vehículo agregado"); }
      setShow(false); load();
    } catch (e2) { toast.error(apiErr(e2)); }
  };
  const del = async (v) => {
    if (!window.confirm(`¿Eliminar vehículo ${v.patente}?`)) return;
    try { await api.delete(`/vehiculos/${v.id}`); toast.success("Eliminado"); load(); } catch (e2) { toast.error(apiErr(e2)); }
  };

  return (
    <>
      <Header title="Vehículos" subtitle="Flota de reparto" onMenu={() => setMobileOpen(true)}
        action={<button onClick={openNew} data-testid="new-vehiculo-btn" className="flex items-center gap-2 text-sm font-semibold bg-[#0A1F44] hover:bg-[#132D5C] text-white rounded-lg px-3.5 py-2"><Plus className="h-4 w-4" /> Nuevo vehículo</button>} />
      <main className="p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehiculos.map((v) => (
            <div key={v.id} className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center"><Car className="h-5 w-5 text-[#3B82F6]" /></div>
                  <div>
                    <div className="font-bold text-[#0A1F44] uppercase">{v.patente}</div>
                    <div className="text-xs text-[#64748B]">{v.tipo}{v.modelo ? ` · ${v.modelo}` : ""}</div>
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${v.estado === "activo" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{v.estado}</span>
              </div>
              {v.repartidor_nombre && <div className="mt-3 text-sm text-[#64748B]">Asignado a <b className="text-[#0A1F44]">{v.repartidor_nombre}</b></div>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => openEdit(v)} className="flex-1 text-xs font-semibold border border-slate-300 rounded-lg py-2 hover:bg-slate-50">Editar</button>
                <button onClick={() => del(v)} className="p-2 rounded-lg text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {vehiculos.length === 0 && <div className="col-span-full text-center py-12 text-[#64748B] bg-white rounded-xl border border-[#E2E8F0]">No hay vehículos registrados.</div>}
        </div>
      </main>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Editar vehículo" : "Nuevo vehículo"}</DialogTitle><DialogDescription>Registrá un vehículo de la flota.</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <input className={input} placeholder="Patente" value={form.patente} onChange={(e) => setForm({ ...form, patente: e.target.value })} required data-testid="vehiculo-patente-input" />
            <div className="grid grid-cols-2 gap-3">
              <select className={input} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                <option value="moto">Moto</option><option value="auto">Auto</option><option value="camioneta">Camioneta</option><option value="camion">Camión</option>
              </select>
              <select className={input} value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                <option value="activo">Activo</option><option value="mantenimiento">Mantenimiento</option><option value="inactivo">Inactivo</option>
              </select>
            </div>
            <input className={input} placeholder="Modelo (opcional)" value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} />
            <select className={input} value={form.repartidor_id} onChange={(e) => setForm({ ...form, repartidor_id: e.target.value })}>
              <option value="">Sin asignar</option>
              {repartidores.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <button className="w-full rounded-lg bg-[#0A1F44] text-white font-semibold py-2.5 text-sm" data-testid="vehiculo-submit-btn">{editing ? "Guardar" : "Agregar"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
